/**
 * Per-stage affordance gating for the WP-129 3-step turn-actions panel.
 *
 * Owns the disabled-state tooltip precedence (stage → resource → structural)
 * for every stage-gated affordance in `<TurnActionBar>` and the play-surface
 * children. Components consume the returned `reason` directly — they do
 * NOT compose tooltips ad-hoc per EC-132 §3.
 *
 * The 3-step turn structure per `DESIGN-BOARD-LAYOUT.md §5.1`:
 *   Step 1 (`play.start`)   → reveal villain
 *   Step 2 (`play.main`)    → play / recruit / fight
 *   Step 3 (`play.cleanup`) → pass priority (advanceStage) / end turn
 *
 * @see WP-129 §Acceptance Criteria — stage gating
 * @see EC-132 §2 3-step turn structure + move-name table
 * @see EC-132 §3 disabled-state tooltip precedence
 * @see DESIGN-BOARD-LAYOUT.md §5.1
 */

import type { GatingResult } from './useCardCostGating';

export type TurnStage = 'start' | 'main' | 'cleanup';
export type TurnStep = 1 | 2 | 3;

const ALLOWED: GatingResult = { allowed: true, reason: null };

const STAGE_DISPLAY_NAMES: Record<TurnStage, string> = {
  start: 'Start (Reveal)',
  main: 'Main (Play / Recruit / Fight)',
  cleanup: 'Cleanup (End Turn)',
};

/**
 * Resolve the active turn step from the engine's `currentStage`. Used by
 * `<TurnActionBar>` to render only the active step at full prominence.
 *
 * // why: stage-to-step mapping is locked at EC-132 §2 and at
 * `DESIGN-BOARD-LAYOUT.md §5.1`. Returning a `TurnStep` rather than a
 * raw stage avoids template branches re-deriving the mapping.
 */
export function activeStepFor(currentStage: string): TurnStep {
  if (currentStage === 'start') {
    return 1;
  }
  if (currentStage === 'main') {
    return 2;
  }
  return 3;
}

/**
 * The heal lock (D-24180): once the viewer has used the Wound Healing ability
 * this turn, the engine silently refuses `recruitHero`, `recruitOfficer`,
 * `fightVillain` and `fightMastermind` for the rest of the turn. Board buttons
 * layer this after the turn + stage gate so a live button never offers a move
 * the engine will no-op.
 *
 * // why: D-24614 — exported as a standalone predicate so the board components
 * (HQRow, CityRow, MastermindTile, SharedDecks), which call `useTurnActions`
 * with only the stage args, share this one reason string instead of threading
 * `hasHealedThisTurn` through fifteen positional parameters.
 *
 * @param hasHealedThisTurn Whether the viewer has healed this turn
 *   (`UIState.game.hasHealedThisTurn`).
 * @returns Blocked with a full-sentence reason when healed, otherwise allowed.
 */
export function healLockGate(hasHealedThisTurn: boolean): GatingResult {
  if (hasHealedThisTurn) {
    return {
      allowed: false,
      reason: 'You cannot recruit or fight after healing this turn.',
    };
  }
  return ALLOWED;
}

// why: D-24589 / D-24769 — four cards park this choice with different caps and filters (Loki's
// Maniacal Tyrant: up to four; Salomé and Destroyer: up to two, Destroyer grey only; Frost
// Giant Warrior and Ymir: one grey Hero). This gate sees only that a choice is pending, so
// the reason names no count; the prompt beside it says how many and which cards.
const KO_DISCARD_CHOICE_REASON =
  'KO cards from your discard pile, or KO None, before taking another action.';

function stageGateReason(currentStage: string, allowedStage: TurnStage): string {
  const allowedDisplay = STAGE_DISPLAY_NAMES[allowedStage];
  return `Only available during the ${allowedDisplay} step (current: ${currentStage}).`;
}

const NOT_YOUR_TURN: GatingResult = {
  allowed: false,
  reason: 'It is not your turn.',
};

/**
 * Composable exposing per-button gating predicates for `<TurnActionBar>`.
 * The returned object's keys map 1:1 to the locked move table in
 * EC-132 §2:
 *
 *   - `canRevealVillain`   → `revealVillainCard` at `play.start`
 *   - `canPlayCard`        → `playCard` at `play.main`
 *   - `canFightVillain`    → `fightVillain` at `play.main`
 *   - `canRecruitHero`     → `recruitHero` at `play.main`
 *   - `canRecruitOfficer`  → `recruitOfficer` at `play.main` (WP-648)
 *   - `canRecruitSidekick` → `recruitSidekick` at `play.main` (WP-842)
 *   - `canFightMastermind` → `fightMastermind` at `play.main`
 *   - `canPassPriority`    → `advanceStage` at any stage (canonical
 *                             stage-advance per D-10011)
 *   - `canEndTurn`         → `endTurn` at `play.cleanup`
 *
 * Each predicate returns a {@link GatingResult}; resource and structural
 * conditions compose on top of the stage-gating reason via the locked
 * precedence (turn → stage → resource → structural).
 *
 * @param currentStage The engine's G.currentStage value.
 * @param isViewerTurn Whether it is currently the viewing player's turn.
 *   When false, all action gates return disabled. Defaults to true for
 *   backwards compatibility with callers that don't pass it.
 * @param hasPendingChoice Whether the viewer has an unresolved hero choice
 *   (derived from `UIState.pendingHeroChoice !== undefined` at the call site).
 *   When true and `currentStage === 'cleanup'`, blocks `canEndTurn` and
 *   `canPassPriority`. Defaults to false — existing callers unaffected.
 * @param hasPendingKoChoice Whether the viewer has an unresolved KO-a-Hero choice
 *   (derived from `UIState.pendingKoHeroChoice !== undefined` at the call site).
 *   When true, blocks `canEndTurn` and `canPassPriority` at ANY stage. Defaults
 *   to false. When both pending choices are active, KO gate reason takes precedence.
 * @param hasPendingOptionalKoReward Whether the viewer has an unresolved
 *   optional-KO-then-reward choice (derived from
 *   `UIState.pendingOptionalKoReward !== undefined` at the call site). When true,
 *   blocks `canEndTurn` and `canPassPriority` at ANY stage (the choice freezes the
 *   board, mirroring `hasPendingKoChoice`). Defaults to false. WP-248's block-all
 *   guard guarantees at most one pending-choice type is active at a time.
 * @param hasPendingDrawOrEmpowered Whether the viewer has an unresolved
 *   draw-or-empowered choice (derived from `UIState.pendingDrawOrEmpowered !== undefined`
 *   at the call site). When true, blocks `canEndTurn` and `canPassPriority` at ANY stage
 *   (WP-286's block-all guard freezes the board, mirroring `hasPendingOptionalKoReward`).
 *   Defaults to false. WP-286's block-all guard guarantees at most one pending-choice
 *   type is active at a time.
 * @param hasPendingVictoryPileCardPick Whether the viewer has an unresolved
 *   victory-pile villain-pick choice (derived from
 *   `UIState.pendingVictoryPileCardPick !== undefined`). When true, blocks
 *   `canEndTurn` and `canPassPriority` at ANY stage. Defaults to false.
 * @param hasPendingOptionalPutBottomHQ Whether the viewer has an unresolved
 *   optional-put-bottom-hq choice (derived from
 *   `UIState.pendingOptionalPutBottomHQ !== undefined` at the call site). When true,
 *   blocks `canEndTurn` and `canPassPriority` at ANY stage (the engine's advanceStage
 *   block-all guard freezes the board, mirroring `hasPendingVictoryPileCardPick`).
 *   Defaults to false. The block-all guard guarantees at most one pending-choice type
 *   is active at a time.
 * @param hasPendingPutAnyNumberBottomHQ Whether the viewer has an unresolved
 *   put-any-number-bottom-hq choice (derived from
 *   `UIState.pendingPutAnyNumberBottomHQ !== undefined` at the call site). When true,
 *   blocks `canEndTurn` and `canPassPriority` at ANY stage (D-24132 — the engine's advanceStage
 *   block-all guard freezes the board, mirroring `hasPendingOptionalPutBottomHQ`). Defaults to
 *   false. The block-all guard guarantees at most one pending-choice type is active at a time.
 * @param hasPendingReturnZeroCostDiscard Whether the viewer has an unresolved
 *   return-zero-cost-discard choice (derived from
 *   `UIState.pendingReturnZeroCostDiscard !== undefined` at the call site). When true,
 *   blocks `canEndTurn` and `canPassPriority` at ANY stage (D-24139 — the engine's full
 *   block-all guard set freezes the board, mirroring `hasPendingKoChoice`). Defaults to
 *   false. The choice is mandatory, so the reason names no decline exit.
 * @param hasPendingDiscardToPlay Whether the viewer has an unresolved
 *   discard-to-play cost (from `UIState.pendingDiscardToPlay !== undefined`). Blocks
 *   End Turn / Pass Priority at any stage (the engine's block-all guard set freezes the
 *   board). Defaults to false. The cost is mandatory, so the reason names no decline
 *   exit. WP-383 / D-24184.
 * @param hasPendingScryKoChoice Whether the viewer has an unresolved Doombot scry-KO
 *   choice (from `UIState.pendingScryKoChoice !== undefined`). When true, blocks
 *   `canEndTurn` and `canPassPriority` at ANY stage (the engine's full block-all guard
 *   set freezes the board, mirroring `hasPendingKoChoice`). Defaults to false. The
 *   choice is mandatory, so the reason names no decline exit. WP-470 / D-24282.
 * @param hasWoundInHand Whether the viewer holds at least one Wound in hand
 *   (derived client-side by scanning `viewer.handCards` for the Wound ext_id).
 *   Gates `canHealWounds`. Defaults to false. WP-380.
 * @param hasActedThisTurn Whether the viewer has recruited or fought this turn
 *   (from `UIState.game.hasActedThisTurn`). Bars `canHealWounds`. Defaults to false. WP-380.
 * @param hasHealedThisTurn Whether the viewer has already used Healing this turn
 *   (from `UIState.game.hasHealedThisTurn`). Bars `canHealWounds`, and (D-24180 heal lock,
 *   D-24614) `canFightVillain` / `canRecruitHero` / `canRecruitOfficer` /
 *   `canFightMastermind`. Defaults to false. WP-380.
 * @param hasPendingDiscardChoice Whether the viewer has an unresolved Magneto
 *   discard-to-limit choice (from `UIState.pendingDiscardChoice !== undefined`). When
 *   true, blocks `canEndTurn` and `canPassPriority` at ANY stage (the engine's full
 *   block-all guard set freezes the board, mirroring `hasPendingScryKoChoice`).
 *   Defaults to false. WP-476 / D-24284. Appended LAST (after the heal params) so the
 *   existing positional callers stay valid without edits; degrades gracefully (no gate)
 *   when the caller omits it.
 * @param hasPendingReorderChoice Whether the viewer has an unresolved reveal-remainder
 *   reorder choice (from `UIState.pendingReorderChoice !== undefined`). When true, blocks
 *   `canEndTurn` and `canPassPriority` at ANY stage (the engine's full block-all guard set
 *   freezes the board, mirroring `hasPendingDiscardChoice`). Defaults to false. WP-480 /
 *   D-24286. Appended LAST (after `hasPendingDiscardChoice`) so existing positional callers
 *   stay valid without edits; degrades gracefully (no gate) when the caller omits it.
 * @param hasPendingDefeatChoice Whether the viewer has an unresolved Silent Sniper
 *   defeat-with-a-Bystander choice (from `UIState.pendingDefeatChoice !== undefined`). When
 *   true, blocks `canEndTurn` and `canPassPriority` at ANY stage (the engine's full block-all
 *   guard set freezes the board, mirroring `hasPendingReorderChoice`). Defaults to false.
 *   WP-486 / D-24291. Appended LAST (after `hasPendingReorderChoice`) so existing positional
 *   callers stay valid without edits; degrades gracefully (no gate) when the caller omits it.
 */
export function useTurnActions(
  currentStage: string,
  isViewerTurn: boolean = true,
  hasPendingChoice: boolean = false,
  hasPendingKoChoice: boolean = false,
  hasPendingOptionalKoReward: boolean = false,
  hasPendingDrawOrEmpowered: boolean = false,
  hasPendingVictoryPileCardPick: boolean = false,
  hasPendingOptionalPutBottomHQ: boolean = false,
  hasPendingPutAnyNumberBottomHQ: boolean = false,
  hasPendingReturnZeroCostDiscard: boolean = false,
  hasPendingDiscardToPlay: boolean = false,
  hasPendingScryKoChoice: boolean = false,
  hasWoundInHand: boolean = false,
  hasActedThisTurn: boolean = false,
  hasHealedThisTurn: boolean = false,
  hasPendingDiscardChoice: boolean = false,
  hasPendingReorderChoice: boolean = false,
  hasPendingDefeatChoice: boolean = false,
  // why: WP-498 / D-24301 — appended LAST (after hasPendingDefeatChoice) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  hasPendingReturnOnDiscard: boolean = false,
  // why: WP-532 / D-24343 — appended LAST (after hasPendingReturnOnDiscard) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a Paibok Fight give-HQ-Hero choice is pending; blocks End Turn / Pass
  // Priority at ANY stage (the engine's full block-all guard set freezes the board).
  hasPendingGiveHqHeroChoice: boolean = false,
  // why: WP-535 / D-24345 — appended LAST (after hasPendingGiveHqHeroChoice) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a Rogue Copy Powers copy-a-Hero choice is pending; blocks End Turn / Pass
  // Priority at ANY stage (the engine's full block-all guard set freezes the board).
  hasPendingCopyPowersChoice: boolean = false,
  // why: WP-538 / D-24347 — appended LAST (after hasPendingCopyPowersChoice) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a core Dr. Doom put-cards-on-deck choice is pending; blocks End Turn / Pass
  // Priority at ANY stage (the engine's full block-all guard set freezes the board).
  hasPendingPutCardsOnDeckChoice: boolean = false,
  // why: WP-603 / D-24413 — appended LAST (after hasPendingPutCardsOnDeckChoice) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a Melter Fight KO/keep choice is pending; blocks End Turn / Pass Priority at
  // ANY stage (the engine's full block-all guard set freezes the board).
  hasPendingMelterKoChoice: boolean = false,
  // why: WP-663 / D-24474 — appended LAST (after hasPendingMelterKoChoice) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while an Emma Frost Shadowed Thoughts play-top-Villain-Deck choice is pending;
  // blocks End Turn / Pass Priority at ANY stage (the engine's full block-all guard set
  // freezes the board). The choice is OPTIONAL — accept (play for +N Attack) OR decline.
  hasPendingPlayVillainTop: boolean = false,
  // why: WP-676 / D-24492 — appended LAST (after hasPendingPlayVillainTop) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a Smash discard-for-attack choice is pending; blocks End Turn / Pass Priority
  // at ANY stage (the engine's full block-all guard set freezes the board). OPTIONAL —
  // discard a hand card for +N Attack OR decline.
  hasPendingSmashDiscard: boolean = false,
  // why: WP-681 / D-24498 — appended LAST (after hasPendingSmashDiscard) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a Deadpool Do-Over accept/decline choice is pending; blocks End Turn / Pass
  // Priority at ANY stage (the engine's full block-all guard set freezes the board). OPTIONAL —
  // discard the whole hand and draw 4 OR decline.
  hasPendingDoOver: boolean = false,
  // why: WP-693 / D-24510 — appended LAST (after hasPendingDoOver) so existing positional
  // callers stay valid without edits; degrades gracefully (no gate) when omitted. True while
  // a Loki Maniacal Tyrant KO-from-discard choice is pending; blocks End Turn / Pass Priority
  // at ANY stage (the engine's full block-all guard set freezes the board). OPTIONAL — the
  // player may KO 0..4, so "KO None" is the decline path.
  hasPendingKoDiscardChoice: boolean = false,
  // why: WP-695 / D-24512 — appended LAST (after hasPendingKoDiscardChoice) so existing positional
  // callers stay valid without edits; degrades gracefully (no gate) when omitted. True while
  // a Red Skull Ruthless Dictator scry-3 disposition choice is pending; blocks End Turn / Pass
  // Priority at ANY stage (the engine's full block-all guard set freezes the board). Mandatory.
  hasPendingRuthlessDictatorChoice: boolean = false,
  // why: WP-695 / D-24512 — appended LAST (after hasPendingRuthlessDictatorChoice) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a Magneto Electromagnetic Bubble X-Men pick is pending; blocks End Turn / Pass
  // Priority at ANY stage (the engine's full block-all guard set freezes the board). Mandatory.
  hasPendingElectromagneticBubbleChoice: boolean = false,
  // why: appended LAST (after hasPendingElectromagneticBubbleChoice) so existing positional
  // callers stay valid without edits; degrades gracefully (reveal never marked spent, start
  // advance never gated) when omitted. Mirrors UIState.game.villainRevealedThisTurn — whether
  // the active player has spent the mandatory start-of-turn reveal. Gates canRevealVillain
  // (disabled once spent) and canPassPriority (start→main blocked until the reveal, matching
  // the engine advanceStage reveal-first guard in game.ts).
  hasRevealedVillain: boolean = false,
  // why: WP-700 / D-24519 — appended LAST (after hasRevealedVillain) so existing positional
  // callers stay valid without edits; degrades gracefully (no gate) when omitted. True while a
  // put-a-hand-card-on-deck-top choice is pending (Gambit's Stack the Deck + siblings); blocks
  // End Turn / Pass Priority / Heal at ANY stage (the engine's full block-all guard set freezes
  // the board). Mandatory — the player must place one hand card on top of their deck.
  hasPendingPutHandOnDeckTop: boolean = false,
  // why: WP-702 / D-24521 — appended LAST (after hasPendingPutHandOnDeckTop) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a reveal-top discard-or-keep choice is pending (Gambit's Hypnotic Charm +
  // standalone family); blocks End Turn / Pass Priority / Heal at ANY stage (the engine's
  // full block-all guard set freezes the board). Mandatory — the player must disposition
  // every revealed deck top before another action.
  hasPendingRevealTopDispose: boolean = false,
  // why: WP-719 / D-24541 — appended LAST (after hasPendingRevealTopDispose) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a Hawkeye Covering Fire choose-one is pending; blocks End Turn / Pass Priority /
  // Heal at ANY stage (the engine's full block-all guard set freezes the board). Mandatory — the
  // active player must pick draw or discard for each other seat.
  hasPendingCoveringFireChoice: boolean = false,
  // True while a split / dual-faced hero "choose a side" pick is pending; blocks End Turn / Pass
  // Priority / Heal at ANY stage (the engine's block-all guard set freezes the board). Mandatory —
  // the active player must bind a side (WP-725 / D-24546).
  hasPendingSplitFaceChoice: boolean = false,
  // why: WP-753 / D-24580 — appended LAST (after hasPendingSplitFaceChoice) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a reveal-three draw / discard / KO assignment is pending (Crystal of Kadavus,
  // Interplanetary Visitor); blocks End Turn / Pass Priority / Heal at ANY stage (the engine's
  // full block-all guard set freezes the board). Mandatory — every revealed card must be assigned.
  hasPendingRevealThreeAssign: boolean = false,
  // why: WP-682 / D-24648 — appended LAST (after hasPendingRevealThreeAssign) so existing
  // positional callers stay valid without edits; degrades gracefully (no gate) when omitted.
  // True while a WP-684 pending seat choice (Diving Block reveal/decline, Random Acts,
  // Monarch's Decree) is open; blocks End Turn / Pass Priority / Heal at ANY stage, matching
  // the engine's hasPendingSeatChoice block-all guard. The seat prompt renders in normal flow
  // (not a modal), so without this the action bar stayed live over an engine-frozen board.
  hasPendingSeatChoice: boolean = false,
  // why: WP-795 / D-24664 — appended LAST (after hasPendingSeatChoice) so existing positional
  // callers stay valid without edits; degrades gracefully (no gate) when omitted. True while a
  // core Storm Spinning Cyclone move-a-Villain choice is pending; blocks End Turn / Pass Priority /
  // Heal at ANY stage (the engine's full block-all guard set freezes the board). The prompt's
  // Don't move button is the decline exit.
  hasPendingMoveVillainChoice: boolean = false,
): {
  activeStep: TurnStep;
  canRevealVillain: () => GatingResult;
  canPlayCard: () => GatingResult;
  canFightVillain: () => GatingResult;
  canRecruitHero: () => GatingResult;
  canRecruitOfficer: () => GatingResult;
  canRecruitSidekick: () => GatingResult;
  canFightMastermind: () => GatingResult;
  canPassPriority: () => GatingResult;
  canEndTurn: () => GatingResult;
  canHealWounds: () => GatingResult;
} {
  return {
    activeStep: activeStepFor(currentStage),
    canRevealVillain: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (currentStage !== 'start') {
        return { allowed: false, reason: stageGateReason(currentStage, 'start') };
      }
      // why: the reveal is once per turn (engine villainRevealedThisTurn guard,
      // villainDeck.reveal.ts). Once spent, disable the button with a tooltip so it
      // stops reading as the live action and cannot be re-clicked — the forward
      // action is then Pass priority (or the auto-advance already fired).
      if (hasRevealedVillain) {
        return { allowed: false, reason: 'You have already revealed the villain this turn.' };
      }
      return ALLOWED;
    },
    canPlayCard: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      return currentStage === 'main'
        ? ALLOWED
        : { allowed: false, reason: stageGateReason(currentStage, 'main') };
    },
    canFightVillain: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (currentStage !== 'main') {
        return { allowed: false, reason: stageGateReason(currentStage, 'main') };
      }
      return healLockGate(hasHealedThisTurn);
    },
    canRecruitHero: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (currentStage !== 'main') {
        return { allowed: false, reason: stageGateReason(currentStage, 'main') };
      }
      return healLockGate(hasHealedThisTurn);
    },
    // why: WP-648 — the turn + stage gate for buying a S.H.I.E.L.D. Officer from
    // the shared supply. The resource (≥ 3 recruit) and supply (officersCount > 0)
    // checks layer on top at the SharedDecks call-site via useCardCostGating (the
    // HQRow precedent) so this composable stays free of economy/pile inputs.
    canRecruitOfficer: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (currentStage !== 'main') {
        return { allowed: false, reason: stageGateReason(currentStage, 'main') };
      }
      return healLockGate(hasHealedThisTurn);
    },
    // why: WP-842 / D-24776 — the turn + stage + heal-lock gate for recruiting a
    // Sidekick from the Sidekick Stack, identical to canRecruitOfficer. The
    // once-per-turn, empty-Stack and affordability checks layer on top at the
    // SharedDecks call-site so this composable stays free of economy/pile inputs.
    canRecruitSidekick: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (currentStage !== 'main') {
        return { allowed: false, reason: stageGateReason(currentStage, 'main') };
      }
      return healLockGate(hasHealedThisTurn);
    },
    canFightMastermind: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (currentStage !== 'main') {
        return { allowed: false, reason: stageGateReason(currentStage, 'main') };
      }
      return healLockGate(hasHealedThisTurn);
    },
    // why: D-10011 — Pass-priority fires `advanceStage`, the canonical
    // stage-advance vocabulary. Allowed at every stage (start advances
    // to main; main advances to cleanup; cleanup advances + ends turn
    // per turnLoop.ts). NOT a no-op.
    // why: D-22203 — blocked at cleanup ONLY when hasPendingChoice is true.
    // Start and main must remain passable so the player can advance through
    // stages to reach the cleanup prompt; blocking all stages would prevent
    // the player from ever reaching the choice.
    // why: D-24012 — blocked at ANY stage when hasPendingKoChoice is true
    // (the KO choice freezes the board completely, unlike the cleanup-only
    // hero-reveal gate). When both pending choices are active, KO gate
    // reason takes precedence.
    canPassPriority: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (hasPendingKoChoice) {
        return {
          allowed: false,
          reason: 'Choose a Hero to KO before taking another action.',
        };
      }
      // why: D-24020 — block turn-end while an optional-KO-then-reward choice is
      // pending (the board is frozen by WP-248's block-all guard, like the KO
      // choice); surface a tooltip instead of a silent rejection. Decline is a
      // first-class exit, so the reason names it.
      if (hasPendingOptionalKoReward) {
        return {
          allowed: false,
          reason: 'Choose a card to KO or Decline before taking another action.',
        };
      }
      // why: WP-676 / D-24492 — block turn-end / pass-priority while a Smash
      // discard-for-attack choice is pending (WP-676's block-all guard freezes the
      // board, mirroring hasPendingOptionalKoReward). Decline is a first-class exit.
      if (hasPendingSmashDiscard) {
        return {
          allowed: false,
          reason: 'Resolve the discard choice (discard a card or Decline) before taking another action.',
        };
      }
      // why: WP-681 / D-24498 — block turn-end / pass-priority while a Do-Over choice is
      // pending (the block-all guard freezes the board, mirroring hasPendingSmashDiscard).
      // Decline is a first-class exit, so the reason names it.
      if (hasPendingDoOver) {
        return {
          allowed: false,
          reason: 'Take the Do-Over (discard your hand and draw 4) or Decline before taking another action.',
        };
      }
      // why: D-24071 — End Turn / Pass Priority blocked at any stage while a
      // draw-or-empowered choice is pending (WP-286's block-all guard freezes the
      // board, mirroring hasPendingOptionalKoReward).
      if (hasPendingDrawOrEmpowered) {
        return {
          allowed: false,
          reason: 'Choose Draw a card or Empowered before taking another action.',
        };
      }
      // why: WP-313 / D-24099 — End Turn / Pass Priority blocked at any stage while a
      // victory-pile villain pick is pending (WP-285's block-all guard freezes the
      // board, mirroring hasPendingDrawOrEmpowered).
      if (hasPendingVictoryPileCardPick) {
        return {
          allowed: false,
          reason: 'Pick a Villain from your Victory Pile before taking another action.',
        };
      }
      // why: End Turn / Pass Priority blocked at any stage while an optional-put-bottom-hq
      // choice is pending (the engine's advanceStage block-all guard freezes the board,
      // mirroring hasPendingVictoryPileCardPick). Decline is a first-class exit, so the
      // reason names it.
      if (hasPendingOptionalPutBottomHQ) {
        return {
          allowed: false,
          reason: 'Put a card from the HQ on the bottom of the Hero Deck, or Decline, before taking another action.',
        };
      }
      // why: D-24132 — End Turn / Pass Priority blocked at any stage while a put-any-number-
      // bottom-hq multi-select choice is pending (the engine's advanceStage block-all guard
      // freezes the board, mirroring hasPendingOptionalPutBottomHQ). Put None is a first-class
      // exit, so the reason names it.
      if (hasPendingPutAnyNumberBottomHQ) {
        return {
          allowed: false,
          reason: 'Choose any number of cards from the HQ to put on the bottom, or Put None, before taking another action.',
        };
      }
      // why: D-24139 — End Turn / Pass Priority blocked at any stage while a
      // return-zero-cost-discard choice is pending (the engine's full block-all guard
      // set freezes the board). The choice is mandatory — no decline exit to name.
      if (hasPendingReturnZeroCostDiscard) {
        return {
          allowed: false,
          reason: 'Return a 0-cost card from your discard pile to your hand before taking another action.',
        };
      }
      // why: WP-383 / D-24184 — End Turn / Pass Priority blocked at any stage while a
      // discard-to-play cost is pending (the engine's full block-all guard set freezes
      // the board). The cost is mandatory — no decline exit to name.
      if (hasPendingDiscardToPlay) {
        return {
          allowed: false,
          reason: 'Discard a card from your hand to complete the play before taking another action.',
        };
      }
      // why: WP-470 / D-24282 — End Turn / Pass Priority blocked at any stage while a
      // Doombot scry-KO choice is pending (the engine's full block-all guard set freezes
      // the board, mirroring hasPendingKoChoice). The choice is mandatory — no decline.
      if (hasPendingScryKoChoice) {
        return {
          allowed: false,
          reason: 'Choose one of the top two cards to KO before taking another action.',
        };
      }
      // why: WP-603 / D-24413 — End Turn / Pass Priority blocked at any stage while a
      // Melter Fight KO/keep choice is pending (the engine's full block-all guard set
      // freezes the board, mirroring hasPendingScryKoChoice). The choice is mandatory.
      if (hasPendingMelterKoChoice) {
        return {
          allowed: false,
          reason: 'Resolve Melter — KO or keep each revealed deck top before taking another action.',
        };
      }
      // why: WP-695 / D-24512 — End Turn / Pass Priority blocked at any stage while a Ruthless
      // Dictator scry-3 choice is pending (the engine's full block-all guard set freezes the
      // board, mirroring hasPendingMelterKoChoice). The choice is mandatory — no decline exit.
      if (hasPendingRuthlessDictatorChoice) {
        return {
          allowed: false,
          reason: 'Ruthless Dictator — KO / discard / keep each revealed card before taking another action.',
        };
      }
      // why: WP-695 / D-24512 — End Turn / Pass Priority blocked at any stage while an
      // Electromagnetic Bubble X-Men pick is pending (the engine's full block-all guard set
      // freezes the board). The choice is mandatory — no decline exit to name.
      if (hasPendingElectromagneticBubbleChoice) {
        return {
          allowed: false,
          reason: 'Choose an X-Men Hero to add to your next hand before taking another action.',
        };
      }
      // why: WP-700 / D-24519 — End Turn / Pass Priority blocked at any stage while a
      // put-a-hand-card-on-deck-top choice is pending (the engine's full block-all guard set
      // freezes the board). The choice is mandatory — no decline exit to name.
      if (hasPendingPutHandOnDeckTop) {
        return {
          allowed: false,
          reason: 'Put a card from your hand on top of your deck before taking another action.',
        };
      }
      // why: WP-702 / D-24521 — End Turn / Pass Priority blocked at any stage while a
      // reveal-top discard-or-keep choice is pending (the engine's full block-all guard set
      // freezes the board, mirroring hasPendingMelterKoChoice). The choice is mandatory.
      if (hasPendingRevealTopDispose) {
        return {
          allowed: false,
          reason: 'Resolve each revealed deck top before taking another action.',
        };
      }
      // why: WP-719 / D-24541 — End Turn / Pass Priority blocked at any stage while a Covering
      // Fire choose-one is pending (the engine's full block-all guard set freezes the board). The
      // choice is mandatory — no decline exit to name.
      if (hasPendingCoveringFireChoice) {
        return {
          allowed: false,
          reason: 'Choose Covering Fire — each other player draws or discards a card — before taking another action.',
        };
      }
      // why: WP-725 / D-24546 — End Turn / Pass Priority blocked at any stage while a split-face
      // "choose a side" pick is pending (the engine's block-all guard set freezes the board). The
      // choice is mandatory — no decline exit to name.
      if (hasPendingSplitFaceChoice) {
        return {
          allowed: false,
          reason: 'Choose a side of the split hero card before taking another action.',
        };
      }
      // why: WP-753 / D-24580 — End Turn / Pass Priority blocked at any stage while a
      // reveal-three draw / discard / KO assignment is pending (the engine's full block-all guard
      // set freezes the board). The assignment is mandatory — no decline exit to name.
      if (hasPendingRevealThreeAssign) {
        return {
          allowed: false,
          reason: 'Draw, discard or KO each revealed card before taking another action.',
        };
      }
      // why: WP-682 / D-24648 — End Turn / Pass Priority blocked at any stage while a WP-684
      // seat choice (Diving Block reveal/decline, Random Acts, Monarch's Decree) is pending
      // (the engine's block-all guard returns early on hasPendingSeatChoice). The prompt
      // renders in normal flow, not a modal, so without this the bar stayed live over a
      // frozen board.
      if (hasPendingSeatChoice) {
        return {
          allowed: false,
          reason: 'Resolve the pending seat choice before taking another action.',
        };
      }
      // why: WP-795 / D-24664 — End Turn / Pass Priority blocked at any stage while a Spinning
      // Cyclone move-a-Villain choice is pending (the engine's full block-all guard set freezes the
      // board). The prompt's Don't move button is the decline exit.
      if (hasPendingMoveVillainChoice) {
        return {
          allowed: false,
          reason: 'Resolve Spinning Cyclone — move a Villain or choose not to — before taking another action.',
        };
      }
      // why: WP-476 / D-24284 — End Turn / Pass Priority blocked at any stage while a
      // Magneto discard-to-limit choice is pending (the engine's full block-all guard
      // set freezes the board, mirroring hasPendingScryKoChoice). The choice is
      // mandatory — no decline exit to name.
      if (hasPendingDiscardChoice) {
        return {
          allowed: false,
          reason: 'Choose which cards to discard before taking another action.',
        };
      }
      // why: WP-480 / D-24286 — End Turn / Pass Priority blocked at any stage while a
      // reveal-remainder reorder choice is pending (the engine's full block-all guard set
      // freezes the board, mirroring hasPendingDiscardChoice). The choice is mandatory —
      // no decline exit to name.
      if (hasPendingReorderChoice) {
        return {
          allowed: false,
          reason: 'Choose the order to put the cards back on top before taking another action.',
        };
      }
      // why: WP-486 / D-24291 — End Turn / Pass Priority blocked at any stage while a
      // Silent Sniper defeat-with-a-Bystander choice is pending (the engine's full
      // block-all guard set freezes the board, mirroring hasPendingReorderChoice). The
      // choice is mandatory — no decline exit to name.
      if (hasPendingDefeatChoice) {
        return {
          allowed: false,
          reason: 'Choose which Villain or Mastermind to defeat before taking another action.',
        };
      }
      // why: WP-498 / D-24301 — End Turn / Pass Priority blocked at any stage while an
      // OPTIONAL return-on-discard choice is pending (the engine's full block-all guard
      // set freezes the board). The choice is optional — Return the card OR Decline.
      if (hasPendingReturnOnDiscard) {
        return {
          allowed: false,
          reason: 'Return the discarded card to your hand or decline before taking another action.',
        };
      }
      // why: WP-532 / D-24343 — End Turn / Pass Priority blocked at any stage while a
      // Paibok Fight give-HQ-Hero choice is pending (the engine's full block-all guard
      // set freezes the board). The choice is mandatory — no decline exit to name.
      if (hasPendingGiveHqHeroChoice) {
        return {
          allowed: false,
          reason: 'Choose a Hero from the HQ for each player to gain before taking another action.',
        };
      }
      // why: WP-535 / D-24345 — End Turn / Pass Priority blocked at any stage while a
      // Rogue Copy Powers copy-a-Hero choice is pending (the engine's full block-all guard
      // set freezes the board). The choice is mandatory — no decline exit to name.
      if (hasPendingCopyPowersChoice) {
        return {
          allowed: false,
          reason: 'Choose a Hero to copy before taking another action.',
        };
      }
      if (hasPendingPutCardsOnDeckChoice) {
        // why: WP-538 / D-24347 — a core Dr. Doom put-cards-on-deck choice is pending
        // (the engine's full block-all guard set freezes the board). The choice is
        // mandatory — no decline exit to name.
        return {
          allowed: false,
          reason: 'Put cards on top of your deck before taking another action.',
        };
      }
      // why: WP-663 / D-24474 — End Turn / Pass Priority blocked at any stage while a
      // Shadowed Thoughts play-top-Villain-Deck choice is pending (the engine's full
      // block-all guard set freezes the board). The choice is OPTIONAL — accept OR decline.
      if (hasPendingPlayVillainTop) {
        return {
          allowed: false,
          reason: 'Play the top card of the Villain Deck, or Decline, before taking another action.',
        };
      }
      if (hasPendingKoDiscardChoice) {
        // why: WP-693 / D-24510 — End Turn / Pass Priority blocked at any stage while a
        // KO-from-discard choice is pending (the engine's full block-all guard set freezes
        // the board). OPTIONAL — "KO None" is the decline path.
        return {
          allowed: false,
          reason: KO_DISCARD_CHOICE_REASON,
        };
      }
      if (currentStage === 'cleanup' && hasPendingChoice) {
        return {
          allowed: false,
          reason: 'Resolve the revealed card choice before ending your turn.',
        };
      }
      // why: the start-of-turn villain reveal is mandatory — the engine's advanceStage
      // blocks start→main until villainRevealedThisTurn (game.ts reveal-first guard).
      // Surface that as a tooltip instead of a silent no-op so a premature Pass-priority
      // at the reveal step explains itself rather than looking broken. Advancing is
      // otherwise allowed at every stage (D-10011); at start, revealing is the way
      // forward (and it auto-advances). Placed LAST so a genuine board-frozen pending
      // choice (the guards above) takes precedence — though start+unrevealed+pending is
      // not a reachable state (the reveal that could park a choice also sets the flag).
      if (currentStage === 'start' && !hasRevealedVillain) {
        return {
          allowed: false,
          reason: 'Reveal the villain to begin your turn — it advances you to Play automatically.',
        };
      }
      return ALLOWED;
    },
    canEndTurn: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (hasPendingKoChoice) {
        // why: D-24012 — the engine's dual turn-end guard (WP-242) blocks
        // endTurn when pendingKoHeroChoices queue is non-empty; this
        // client-side gate surfaces the reason so the player sees a tooltip
        // instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose a Hero to KO before taking another action.',
        };
      }
      if (hasPendingOptionalKoReward) {
        // why: D-24020 — WP-248's block-all turn-end guard blocks endTurn while
        // pendingOptionalKoRewards is non-empty; this client-side gate surfaces
        // the reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose a card to KO or Decline before taking another action.',
        };
      }
      if (hasPendingSmashDiscard) {
        // why: WP-676 / D-24492 — WP-676's block-all turn-end guard blocks endTurn while
        // pendingSmashDiscards is non-empty; this client-side gate surfaces the reason so
        // the player sees a tooltip instead of a silent rejection. OPTIONAL — discard OR decline.
        return {
          allowed: false,
          reason: 'Resolve the discard choice (discard a card or Decline) before taking another action.',
        };
      }
      if (hasPendingDoOver) {
        // why: WP-681 / D-24498 — the block-all turn-end guard blocks endTurn while
        // pendingDoOverChoices is non-empty; this client-side gate surfaces the reason so
        // the player sees a tooltip instead of a silent rejection. OPTIONAL — accept OR decline.
        return {
          allowed: false,
          reason: 'Take the Do-Over (discard your hand and draw 4) or Decline before taking another action.',
        };
      }
      if (hasPendingDrawOrEmpowered) {
        // why: D-24071 — WP-286's block-all turn-end guard blocks endTurn while
        // pendingDrawOrEmpowered is non-empty; this client-side gate surfaces the
        // reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose Draw a card or Empowered before taking another action.',
        };
      }
      if (hasPendingVictoryPileCardPick) {
        // why: WP-313 / D-24099 — WP-285's block-all turn-end guard blocks endTurn
        // while pendingVictoryPileCardPick is non-empty; this client-side gate
        // surfaces the reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Pick a Villain from your Victory Pile before taking another action.',
        };
      }
      if (hasPendingOptionalPutBottomHQ) {
        // why: the engine's advanceStage block-all guard blocks endTurn while
        // pendingOptionalPutBottomHQ is non-empty; this client-side gate surfaces the
        // reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Put a card from the HQ on the bottom of the Hero Deck, or Decline, before taking another action.',
        };
      }
      if (hasPendingPutAnyNumberBottomHQ) {
        // why: D-24132 — the engine's advanceStage block-all guard blocks endTurn while
        // pendingPutAnyNumberBottomHQ is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose any number of cards from the HQ to put on the bottom, or Put None, before taking another action.',
        };
      }
      if (hasPendingReturnZeroCostDiscard) {
        // why: D-24139 — the engine's block-all guards block endTurn while
        // pendingReturnZeroCostDiscard is non-empty; this client-side gate surfaces the
        // reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Return a 0-cost card from your discard pile to your hand before taking another action.',
        };
      }
      if (hasPendingDiscardToPlay) {
        // why: WP-383 / D-24184 — the engine's block-all guards block endTurn while
        // pendingDiscardToPlay is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Discard a card from your hand to complete the play before taking another action.',
        };
      }
      if (hasPendingScryKoChoice) {
        // why: WP-470 / D-24282 — the engine's block-all guards block endTurn while
        // pendingScryKoChoices is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose one of the top two cards to KO before taking another action.',
        };
      }
      if (hasPendingMelterKoChoice) {
        // why: WP-603 / D-24413 — the engine's block-all guards block endTurn while
        // pendingMelterKoChoices is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Resolve Melter — KO or keep each revealed deck top before taking another action.',
        };
      }
      if (hasPendingRevealTopDispose) {
        // why: WP-702 / D-24521 — the engine's block-all guards block endTurn while
        // pendingRevealTopDispose is non-empty; surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Resolve each revealed deck top before taking another action.',
        };
      }
      if (hasPendingCoveringFireChoice) {
        // why: WP-719 / D-24541 — the engine's block-all guards block endTurn while
        // pendingCoveringFireChoices is non-empty; surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Choose Covering Fire — each other player draws or discards a card — before taking another action.',
        };
      }
      if (hasPendingSplitFaceChoice) {
        // why: WP-725 / D-24546 — the engine's block-all guards block endTurn/heal while
        // pendingSplitFaceChoices is non-empty; surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Choose a side of the split hero card before taking another action.',
        };
      }
      if (hasPendingRevealThreeAssign) {
        // why: WP-753 / D-24580 — the engine's block-all guards block endTurn while
        // pendingRevealThreeAssign is non-empty; surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Draw, discard or KO each revealed card before taking another action.',
        };
      }
      if (hasPendingSeatChoice) {
        // why: WP-682 / D-24648 — the engine's block-all guards block endTurn while
        // G.pendingSeatChoice is set (Diving Block / Random Acts / Monarch's Decree);
        // surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Resolve the pending seat choice before taking another action.',
        };
      }
      if (hasPendingMoveVillainChoice) {
        // why: WP-795 / D-24664 — the engine's block-all guards block endTurn while
        // pendingMoveVillainChoices is non-empty; surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Resolve Spinning Cyclone — move a Villain or choose not to — before taking another action.',
        };
      }
      if (hasPendingRuthlessDictatorChoice) {
        // why: WP-695 / D-24512 — the engine's block-all guards block endTurn while
        // pendingRuthlessDictatorChoices is non-empty; surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Ruthless Dictator — KO / discard / keep each revealed card before taking another action.',
        };
      }
      if (hasPendingElectromagneticBubbleChoice) {
        // why: WP-695 / D-24512 — the engine's block-all guards block endTurn while
        // pendingElectromagneticBubbleChoices is non-empty; surface the reason as a tooltip.
        return {
          allowed: false,
          reason: 'Choose an X-Men Hero to add to your next hand before taking another action.',
        };
      }
      if (hasPendingDiscardChoice) {
        // why: WP-476 / D-24284 — the engine's block-all guards block endTurn while
        // pendingDiscardChoices is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose which cards to discard before taking another action.',
        };
      }
      if (hasPendingReorderChoice) {
        // why: WP-480 / D-24286 — the engine's block-all guards block endTurn while
        // pendingReorderChoices is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose the order to put the cards back on top before taking another action.',
        };
      }
      if (hasPendingDefeatChoice) {
        // why: WP-486 / D-24291 — the engine's block-all guards block endTurn while
        // pendingDefeatChoices is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose which Villain or Mastermind to defeat before taking another action.',
        };
      }
      if (hasPendingGiveHqHeroChoice) {
        // why: WP-532 / D-24343 — the engine's block-all guards block endTurn while
        // pendingGiveHqHeroChoice is set (Paibok Fight); this client-side gate surfaces
        // the reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose a Hero from the HQ for each player to gain before taking another action.',
        };
      }
      if (hasPendingCopyPowersChoice) {
        // why: WP-535 / D-24345 — the engine's block-all guards block endTurn while
        // pendingCopyPowersChoices is non-empty (Rogue's Copy Powers); this client-side
        // gate surfaces the reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Choose a Hero to copy before taking another action.',
        };
      }
      if (hasPendingPutCardsOnDeckChoice) {
        // why: WP-538 / D-24347 — the engine's block-all guards block endTurn while
        // pendingPutCardsOnDeckChoices is non-empty (core Dr. Doom); this client-side gate
        // surfaces the reason so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: 'Put cards on top of your deck before taking another action.',
        };
      }
      if (hasPendingPlayVillainTop) {
        // why: WP-663 / D-24474 — the engine's block-all guards block endTurn while
        // pendingPlayVillainTopChoices is non-empty (Emma Frost's Shadowed Thoughts); this
        // client-side gate surfaces the reason so the player sees a tooltip instead of a
        // silent rejection. The choice is OPTIONAL — accept OR decline.
        return {
          allowed: false,
          reason: 'Play the top card of the Villain Deck, or Decline, before taking another action.',
        };
      }
      if (hasPendingKoDiscardChoice) {
        // why: WP-693 / D-24510 — the engine's block-all guards block endTurn while
        // pendingKoDiscardChoices is non-empty; this client-side gate surfaces the reason
        // so the player sees a tooltip instead of a silent rejection.
        return {
          allowed: false,
          reason: KO_DISCARD_CHOICE_REASON,
        };
      }
      if (hasPendingChoice) {
        // why: D-22203 — the engine's dual turn-end guard (WP-220) blocks
        // endTurn when pendingHeroChoice is set; this client-side gate
        // surfaces the reason so the player sees a tooltip instead of a
        // silent rejection.
        // why (Jeff feedback): now checked at EVERY stage, not just cleanup —
        // End turn is a one-click terminator that fires from the main stage too
        // (the client chains advanceStage → endTurn), so a pending hero-reveal
        // choice must block it wherever the player clicks, not only at cleanup.
        return {
          allowed: false,
          reason: 'Resolve the revealed card choice before ending your turn.',
        };
      }
      // why (Jeff feedback): End turn is now the single forward action for the whole
      // play part of the turn — the "Pass priority" button is gone. It fires from
      // the MAIN stage (TurnActionBar chains advanceStage → endTurn, mirroring the
      // reveal auto-advance) as well as the cleanup stage, so allow both here. At
      // 'start' it stays blocked: the villain reveal (which auto-advances to main)
      // is the way forward, not ending the turn.
      return currentStage === 'main' || currentStage === 'cleanup'
        ? ALLOWED
        : {
            allowed: false,
            reason: 'Reveal the villain to begin your turn before you can end it.',
          };
    },
    // why: WP-380 / D-24181 — the Wound "Healing" ability (engine healWounds).
    // Precedence: turn → main stage → block-all pending → wound-in-hand →
    // not-acted → not-healed. The pending set MUST mirror the engine
    // healWounds block-all guards EXACTLY (healWounds.ts): every pending-choice
    // type that freezes the board in the move also blocks Healing here, so a live
    // button never offers a heal the engine will silently no-op. EC-565 restores
    // the five guards — discardToPlay (D-24184), discardChoice (D-24284),
    // reorderChoice (D-24286), defeatChoice (D-24291), returnOnDiscard (D-24301) —
    // that drifted out of this gate as each pending choice was added after WP-380.
    canHealWounds: () => {
      if (!isViewerTurn) return NOT_YOUR_TURN;
      if (currentStage !== 'main') {
        return { allowed: false, reason: stageGateReason(currentStage, 'main') };
      }
      if (
        hasPendingKoChoice ||
        hasPendingScryKoChoice ||
        hasPendingMelterKoChoice ||
        // why: WP-702 / D-24521 — mirror the engine healWounds block-all guard, which returns
        // early while a reveal-top discard-or-keep choice is pending.
        hasPendingRevealTopDispose ||
        hasPendingOptionalKoReward ||
        hasPendingVictoryPileCardPick ||
        hasPendingDrawOrEmpowered ||
        hasPendingReturnZeroCostDiscard ||
        hasPendingDiscardToPlay ||
        hasPendingDiscardChoice ||
        hasPendingReorderChoice ||
        hasPendingDefeatChoice ||
        hasPendingReturnOnDiscard ||
        hasPendingGiveHqHeroChoice ||
        hasPendingCopyPowersChoice ||
        hasPendingPutCardsOnDeckChoice ||
        hasPendingPlayVillainTop ||
        // why: WP-676 / D-24492 — mirror the engine healWounds block-all guard, which now
        // returns early while a Smash choice is pending; without this the Heal button would
        // offer a heal the engine silently no-ops.
        hasPendingSmashDiscard ||
        // why: WP-681 / D-24498 — mirror the engine healWounds block-all guard, which returns
        // early while a Do-Over choice is pending.
        hasPendingDoOver ||
        // why: WP-693 / D-24510 — mirror the engine healWounds block-all guard, which returns
        // early while a Loki Maniacal Tyrant KO-from-discard choice is pending.
        hasPendingKoDiscardChoice ||
        // why: WP-695 / D-24512 — mirror the engine healWounds block-all guards, which return
        // early while a Ruthless Dictator scry-3 or Electromagnetic Bubble pick is pending.
        hasPendingRuthlessDictatorChoice ||
        hasPendingElectromagneticBubbleChoice ||
        // why: WP-700 / D-24519 — mirror the engine healWounds block-all guard, which returns
        // early while a put-a-hand-card-on-deck-top choice is pending.
        hasPendingPutHandOnDeckTop ||
        // why: WP-719 / D-24541 — mirror the engine healWounds block-all guard, which returns
        // early while a Covering Fire choose-one is pending.
        hasPendingCoveringFireChoice ||
        // why: WP-725 / D-24546 — mirror the engine healWounds block-all guard, which returns
        // early while a split / dual-faced hero "choose a side" pick is pending.
        hasPendingSplitFaceChoice ||
        // why: WP-753 / D-24580 — mirror the engine healWounds block-all guard, which returns
        // early while a reveal-three draw / discard / KO assignment is pending.
        hasPendingRevealThreeAssign ||
        // why: WP-682 / D-24648 — mirror the engine healWounds block-all guard, which returns
        // early while a WP-684 seat choice (Diving Block / Random Acts / Monarch's Decree) is pending.
        hasPendingSeatChoice ||
        // why: WP-795 / D-24664 — mirror the engine healWounds block-all guard, which returns
        // early while a Spinning Cyclone move-a-Villain choice is pending.
        hasPendingMoveVillainChoice
      ) {
        return {
          allowed: false,
          reason: 'Resolve the pending choice before you can heal.',
        };
      }
      if (!hasWoundInHand) {
        return {
          allowed: false,
          reason: 'You have no Wounds in hand to heal.',
        };
      }
      if (hasActedThisTurn) {
        return {
          allowed: false,
          reason: 'You cannot heal after recruiting or fighting this turn.',
        };
      }
      if (hasHealedThisTurn) {
        return {
          allowed: false,
          reason: 'You have already healed this turn.',
        };
      }
      return ALLOWED;
    },
  };
}
